import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../i18n/LanguageContext';
import { withTxToast } from '../utils/txToast';
import { Droplet, Loader, CheckCircle, AlertCircle, Clock, Copy, Wallet } from 'lucide-react';

const TokenContractInfo = ({ contractAddress, copied, onCopy, onAddToMetaMask }) => {
  const { t } = useLanguage();
  if (!contractAddress) return null;
  return (
    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
      <p className="text-xs font-semibold text-blue-700 mb-2">{t('faucet.contractLabel')}</p>
      <div className="flex items-center space-x-2">
        <code className="flex-1 text-xs text-gray-800 bg-white border border-gray-200 rounded px-2 py-1.5 font-mono truncate">
          {contractAddress}
        </code>
        <button
          onClick={onCopy}
          title={t('faucet.copyAddress')}
          className="flex-shrink-0 p-1.5 rounded hover:bg-blue-100 transition-colors"
        >
          {copied
            ? <CheckCircle className="w-4 h-4 text-green-500" />
            : <Copy className="w-4 h-4 text-blue-500" />}
        </button>
      </div>
      {window.ethereum && (
        <button
          onClick={onAddToMetaMask}
          className="mt-2 w-full flex items-center justify-center space-x-2 text-xs font-semibold bg-white hover:bg-orange-50 text-orange-700 border border-orange-300 rounded-lg py-2 transition-colors"
        >
          <Wallet className="w-4 h-4" />
          <span>{t('faucet.importMetaMask')}</span>
        </button>
      )}
    </div>
  );
};

const Faucet = () => {
  const { web3, account, contract, contractAddress, triggerRefresh } = useWeb3();
  const { t } = useLanguage();
  const [faucetInfo, setFaucetInfo] = useState({
    amount: '0',
    cooldown: 0,
    canRequest: false,
    timeUntilNext: 0,
  });
  const [loading, setLoading] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!contractAddress) return;
    navigator.clipboard.writeText(contractAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAddToMetaMask = async () => {
    if (!window.ethereum || !contractAddress) return;
    try {
      await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: contractAddress,
            symbol: 'BFPE',
            decimals: 18,
          },
        },
      });
    } catch (err) {
      console.error('Error añadiendo token a MetaMask:', err);
    }
  };

  useEffect(() => {
    loadFaucetInfo();
  }, [contract, account]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            loadFaucetInfo();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [countdown]);

  const loadFaucetInfo = async () => {
    if (!contract || !account || !web3) return;

    try {
      const [amount, cooldown, canRequest, timeUntilNext] = await Promise.all([
        contract.methods.faucetAmount().call(),
        contract.methods.faucetCooldown().call(),
        contract.methods.canRequestTokens(account).call(),
        contract.methods.getTimeUntilNextRequest(account).call(),
      ]);

      setFaucetInfo({
        amount: web3.utils.fromWei(amount, 'ether'),
        cooldown: Number(cooldown),
        canRequest,
        timeUntilNext: Number(timeUntilNext),
      });

      setCountdown(Number(timeUntilNext));
    } catch (err) {
      console.error('Error cargando información del faucet:', err);
    }
  };

  const handleRequestTokens = async () => {
    if (!contract || !account) {
      setError(t('faucet.errNoWallet'));
      return;
    }

    if (!faucetInfo.canRequest) {
      setError(t('faucet.errCooldown'));
      return;
    }

    setLoading(true);
    setError('');
    setTxHash('');

    try {
      const tx = await withTxToast(
        () => contract.methods.requestTokens().send({ from: account, gas: 150000, gasPrice: '0' }),
        t('faucet.toastLoading'),
        t('faucet.toastSuccess')
      );

      setTxHash(tx.transactionHash);
      triggerRefresh();
      await loadFaucetInfo();
    } catch (err) {
      setError(err.message || t('faucet.toastError'));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (seconds === 0) return t('faucet.availableNow');
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  if (!account) {
    return (
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <Droplet className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">{t('faucet.title')}</h2>
        </div>
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            {t('faucet.noWallet')}
          </p>
        </div>
        <TokenContractInfo contractAddress={contractAddress} copied={copied} onCopy={handleCopy} onAddToMetaMask={handleAddToMetaMask} />
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-2 mb-6">
        <Droplet className="w-6 h-6 text-purple-600" />
        <h2 className="text-2xl font-bold text-gray-800">{t('faucet.title')}</h2>
      </div>

      <div className="space-y-4">
        {/* Información del Faucet */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">{t('faucet.amountLabel')}</span>
            <span className="text-2xl font-bold text-purple-600">
              {parseFloat(faucetInfo.amount).toLocaleString()} BFPE
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">{t('faucet.cooldownLabel')}</span>
            <span className="text-sm font-semibold text-gray-800">
              {t('faucet.hours', { n: faucetInfo.cooldown / 3600 })}
            </span>
          </div>
        </div>

        {/* Estado de Disponibilidad */}
        {!faucetInfo.canRequest && countdown > 0 && (
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <Clock className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-orange-800 mb-1">
                  {t('faucet.nextRequest')}
                </p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatTime(countdown)}
                </p>
              </div>
            </div>
          </div>
        )}

        {faucetInfo.canRequest && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <p className="text-sm font-medium text-green-800">
                {t('faucet.availableNow')}
              </p>
            </div>
          </div>
        )}

        {/* Mensajes de Error y Éxito */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {txHash && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-green-800 font-medium">
                {t('faucet.toastSuccess')}
              </p>
              <p className="text-xs text-green-700 mt-1 break-all">{t('faucet.successTx', { hash: txHash })}</p>
            </div>
          </div>
        )}

        {/* Botón de Solicitud */}
        <button
          onClick={handleRequestTokens}
          disabled={loading || !faucetInfo.canRequest}
          className="btn-primary w-full flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>{t('transfer.processing')}</span>
            </>
          ) : (
            <>
              <Droplet className="w-5 h-5" />
              <span>{t('faucet.submit', { amount: faucetInfo.amount })}</span>
            </>
          )}
        </button>

        {/* Información Adicional */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600">
            <strong>{t('faucet.infoPrefix')}</strong> {t('faucet.infoText', { hours: faucetInfo.cooldown / 3600 })}
          </p>
        </div>

        <TokenContractInfo contractAddress={contractAddress} copied={copied} onCopy={handleCopy} onAddToMetaMask={handleAddToMetaMask} />
      </div>
    </div>
  );
};

export default Faucet;
